import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { HeaderStyle, SearchContainer } from 'pages/layout/Header.style';
import HeaderProfile from 'pages/layout/HeaderProfile';
import Sidebar from 'pages/sidebar';
import BasicChips from 'components/Chip';
import clsx from 'clsx';
import { Button, Input, Modal } from 'components';
import { InputAdornment, Skeleton } from '@mui/material';
import { handleGetSearchUsersList } from 'redux/features/SearchSlice';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CrossIcon } from 'assert/images/Common/cross.svg';
import { ReactComponent as Notification } from 'assert/images/Navbar/notification.svg';
import { ReactComponent as Profile } from 'assert/images/Common/profileImg.svg';
import { ReactComponent as WafyNetLogo } from 'assert/images/Common/wafynetLogo.svg';
import { ReactComponent as Work } from 'assert/images/Navbar/work.svg';
import { ReactComponent as HomeSearch } from 'assert/images/Home/homeSearch.svg';
import { UserProfile } from '../home/Home.style';
import usePushNotificationListener from 'components/PushNotificationService';

export const SearchUserList = ({
  data,
  loading,
  headline,
  setOpenSearch,
  keyword,
}) => {
  const navigate = useNavigate();

  const highlightText = (text, keyword) => {
    if (!keyword) return text;
    const regex = new RegExp(`(${keyword})`, 'gi');
    return text.replace(regex, "<span class='search-highlight'>$1</span>");
  };

  return (
    <div className="search-user-container">
      <div className="search-user-item">
        {loading ? (
          <Skeleton
            variant="circular"
            width={45}
            height={45}
            animation="wave"
          />
        ) : (
          <UserProfile width={45} height={45}>
            {data?.profileImage ? (
              <img
                src={data?.profileImage}
                className="profile-img"
                alt="user-profile-picture"
              />
            ) : (
              <Profile />
            )}
          </UserProfile>
        )}
        <div className="search-user-details">
          <div className="search-displayName">
            {loading ? (
              <Skeleton width={200} animation="wave" />
            ) : (
              <span
                onClick={() => {
                  navigate(`/profile/${data.username}`);
                  setOpenSearch && setOpenSearch(false);
                }}
              >
                {data.displayName}
              </span>
            )}
          </div>
          <div className="search-username">
            {loading ? (
              <Skeleton width={200} animation="wave" />
            ) : (
              <span
                dangerouslySetInnerHTML={{
                  __html: highlightText(data?.username || '', keyword),
                }}
              />
            )}
          </div>
        </div>
      </div>
      {headline}
    </div>
  );
};

export default function Headers({ notificationCount }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  usePushNotificationListener();
  // const { screenW } = useWindowDimensions();

  const { getProfile } = useSelector((state) => state?.ProfileSlice);

  const [open, setopen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [openSearch, setOpenSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [searchList, setSearchList] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleScroll = () => {
    const currentScrollPos = window.scrollY;
    setVisible(prevScrollPos > currentScrollPos || currentScrollPos < 10);
    setPrevScrollPos(currentScrollPos);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos, visible]);

  const handleNavigatetoHome = () => {
    navigate('/');
    window.scroll(0, 0);
  };

  useEffect(() => {
    if (openSearch) {
      if (search.trim()) {
        setLoading(true);
        const handler = setTimeout(() => {
          const payload = {
            keyword: search,
            page: 0,
            size: 7,
          };
          dispatch(handleGetSearchUsersList(payload))
            .then((res) => {
              if (res?.data?.rows?.data) {
                setSearchList(res?.data?.rows?.data);
              }
              setLoading(false);
            })
            .catch((err) => {
              console.log('searchResErr', err);
              setLoading(false);
            });
        }, 600);

        return () => {
          clearTimeout(handler);
        };
      } else {
        setSearchList([]);
      }
    }
  }, [search]);

  const handleChange = (data) => {
    setSearch(data);
  };

  return (
    <HeaderStyle className={clsx(!visible && 'hidden')}>
      <header className="header">
        <div className="header-container">
          <div className="header-brand" onClick={handleNavigatetoHome}>
            <WafyNetLogo />
            <h3 className="logo-title">WafyNet</h3>
          </div>
          <Modal
            removeHeader
            modalchanges="searchModal"
            handleCancelModal={() => {
              setSearch('');
              setSearchList([]);
              setOpenSearch(false);
            }}
            openModal={openSearch}
            content={
              <SearchContainer>
                <div className="search-action">
                  <Input
                    onChange={(data) => handleChange(data)}
                    onKeyDown={(event) => {
                      if (event?.key === 'Enter' && search.trim()) {
                        navigate(`/search/people?keyword=${encodeURIComponent(search)}`, { replace: true });
                        setSearch('');
                        setSearchList([]);
                        setOpenSearch(false);
                      }
                    }}
                    value={search}
                    placeholder={t('HomePage.wafynetSearch')}
                    autoFocus
                    startAdornment={
                      <InputAdornment>
                        <HomeSearch />
                      </InputAdornment>
                    }
                  />
                  <CrossIcon
                    className="search-close-icon"
                    onClick={() => {
                      setSearch('');
                      setSearchList([]);
                      setOpenSearch(false);
                    }}
                  />
                </div>
                <div className="search-content-container">
                  {searchList?.length === 0 && search?.trim()?.length === 0 && (
                    <div className="search-tip">
                      {t('HomePage.SearchDetail')}
                    </div>
                  )}
                  {searchList?.length > 0 && (
                    <>
                      {searchList.map((data, index) => {
                        return (
                          <div key={index}>
                            <SearchUserList
                              data={data}
                              loading={loading}
                              setOpenSearch={setOpenSearch}
                              keyword={search}
                            />
                          </div>
                        );
                      })}
                    </>
                  )}
                  {!loading &&
                    searchList?.length === 0 &&
                    search?.trim()?.length > 0 && (
                      <div className="no-data-found">
                        <div>{t('HomePage.NoFound')}</div>
                      </div>
                    )}
                </div>
                {!(searchList?.length === 0 && search?.trim()?.length === 0) && (
                  <div className="see-all-results">
                    <Button
                      variant="text"
                      label={t('HomePage.Results')}
                      onClick={() => {
                        navigate(`/search/people?keyword=${encodeURIComponent(search)}`, { replace: true });
                        setOpenSearch(false);
                      }}
                    />
                  </div>
                )}
              </SearchContainer>
            }
          />
          <div className="head-list">
            <div className="header-action-container">
              <div
                className="header-icon-block search-icon"
                onClick={() => setOpenSearch(true)}
              >
                <HomeSearch />
              </div>
              <div
                className="header-icon-block notify-icon"
                onClick={() => navigate('/notification')}
              >
                <Notification />
                {notificationCount?.count ? <span className='notification-count'>{notificationCount?.count}</span> : <></>}
              </div>
              <BasicChips
                onClick={() => navigate('/postjob')}
                icon={<Work />}
                variant="outlined"
                label={t(`HomePage.PostAJob`)}
              />
            </div>
            <HeaderProfile UserDetails={getProfile} />
          </div>
        </div>
      </header>
      <div
        className={`menu-view ${!open && 'd-none'}`}
        onClick={() => setopen(false)}
      >
        <div className="menu-list">
          <Sidebar />
        </div>
      </div>
    </HeaderStyle>
  );
}
